import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSSE } from '../hooks/useSSE';
import { useNavigate, Link } from 'react-router-dom';
import { tablesAPI, ordersAPI, paymentsAPI, stockAPI, reportsAPI, menuAPI, usersAPI, settingsAPI, reservationsAPI, couriersAPI, shiftsAPI, deliveryAPI, auditLogsAPI, hrAPI, statsAPI } from '../services/api';
import SubscriptionBanner from '../components/SubscriptionBanner';
import KPICard from '../components/KPICard';
import ActivityFeed from '../components/ActivityFeed';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import useFocusTrap from '../hooks/useFocusTrap';

// Dashboard widget kataloğu — kullanıcı bu listeden show/hide tercihi yapar.
// id'ler localStorage'a kaydedilir ('dashboard_enabled_widgets').
const DASHBOARD_WIDGETS = [
  { id: 'kpi_main',           tKey: 'dashWidgetKpiMain',         fallback: 'KPI Özet (7 kart)' },
  { id: 'action_items',       tKey: 'dashWidgetActionItems',     fallback: 'Aksiyon Listesi' },
  { id: 'peak_hours',         tKey: 'dashWidgetPeakHours',       fallback: 'Yoğun Saatler' },
  { id: 'order_channels',     tKey: 'dashWidgetOrderChannels',   fallback: 'Sipariş Kanalları' },
  { id: 'waste_summary',      tKey: 'dashWidgetWasteSummary',    fallback: 'Haftalık Ziyan' },
  { id: 'activity_feed',      tKey: 'dashWidgetActivityFeed',    fallback: 'Aktivite Akışı' },
  { id: 'recent_audits',      tKey: 'dashWidgetRecentAudits',    fallback: 'Son Admin İşlemleri' },
  { id: 'doc_expiry',         tKey: 'dashWidgetDocExpiry',       fallback: 'Belge Süreleri' },
  { id: 'top_waiter',         tKey: 'dashWidgetTopWaiter',       fallback: 'Yıldız Personel' },
  { id: 'quick_start',        tKey: 'dashWidgetQuickStart',      fallback: 'Hızlı Başlangıç' },
  { id: 'stats_overview',     tKey: 'dashWidgetStatsOverview',   fallback: 'İstatistik Özeti (6 kart)' },
  { id: 'revenue_chart',      tKey: 'dashWidgetRevenueChart',    fallback: 'Ciro Grafiği' },
  { id: 'top_items',          tKey: 'dashWidgetTopItems',        fallback: 'En Çok Satanlar' },
  { id: 'low_stock_alert',    tKey: 'dashWidgetLowStockAlert',   fallback: 'Stok Uyarıları' },
  { id: 'active_orders',      tKey: 'dashWidgetActiveOrders',    fallback: 'Aktif Siparişler & Günlük Özet' },
  { id: 'recent_activity',    tKey: 'dashWidgetRecentActivity',  fallback: 'Son İşlemler Akışı' },
  { id: 'quick_actions',      tKey: 'dashWidgetQuickActions',    fallback: 'Hızlı Aksiyonlar' },
];
const DASHBOARD_WIDGET_KEY = 'dashboard_enabled_widgets';

// FIX-07: Dashboard Hızlı Başlangıç — API'den masa/menü/personel/restoran-adı
// kontrolü yapılır. Eksikler için CTA gösterilir. Hepsi tamamsa component null
// döner (hiç render edilmez). Yüklenirken skeleton gösterilir.
function QuickStartChecklist({ t, counts, loading, navigate }) {
  const steps = [
    { done: !!counts.restaurantName && !!counts.restaurantPhone, key: 'qsRestaurantInfo', to: '/settings',      icon: '🏢' },
    { done: counts.tables > 0,       key: 'qsAddFirstTable', to: '/tables',        icon: '🍽️' },
    { done: counts.menuItems > 0,    key: 'qsAddMenuItem',   to: '/menu-management', icon: '📖' },
    { done: counts.users > 1,        key: 'qsAddStaff',      to: '/users',         icon: '🧑‍💼' },
  ];
  const remaining = steps.filter(s => !s.done);
  // Yükleme bitmeden "hepsi tamam" varsayımıyla gizleme — flicker'ı önlemek için skeleton göster
  if (!loading && remaining.length === 0) return null;
  const donePct = Math.round(((steps.length - remaining.length) / steps.length) * 100);
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-blue-900 dark:text-blue-200 flex items-center gap-2">
            🚀 {t('quickStartTitle')}
          </h2>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            {t('quickStartSubtitle')}
          </p>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="text-2xl font-black text-blue-700 dark:text-blue-300">{donePct}%</div>
          <div className="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">
            {steps.length - remaining.length}/{steps.length}
          </div>
        </div>
      </div>
      <div className="w-full bg-white/60 dark:bg-blue-950/40 rounded-full h-2 mb-4 overflow-hidden">
        <div className="bg-blue-600 dark:bg-blue-400 h-full rounded-full transition-all duration-500"
          style={{ width: `${donePct}%` }} />
      </div>
      <div className="space-y-2">
        {steps.map(s => (
          <button
            key={s.key}
            onClick={() => !s.done && !loading && navigate(s.to)}
            disabled={s.done || loading}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
              loading
                ? 'bg-white/40 dark:bg-blue-950/20 border border-blue-100/50 dark:border-blue-800/30 animate-pulse'
                : s.done
                ? 'bg-white/50 dark:bg-blue-950/30 text-gray-400 dark:text-gray-500'
                : 'bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 border border-blue-100 dark:border-blue-800/60 active:scale-[0.99]'
            }`}
          >
            <span className="text-xl flex-shrink-0">{loading ? '⏳' : (s.done ? '✅' : s.icon)}</span>
            <span className={`flex-1 text-sm font-medium ${loading ? 'text-gray-400 dark:text-gray-500' : s.done ? 'line-through' : 'text-gray-800 dark:text-gray-100'}`}>
              {t(s.key)}
            </span>
            {!loading && !s.done && <span className="text-blue-500 dark:text-blue-400 text-sm">→</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-800/60 flex items-center justify-between">
        <span className="text-xs text-blue-700 dark:text-blue-400">{t('quickStartOrWizard')}</span>
        <button onClick={() => navigate('/onboarding')}
          className="text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
          🪄 {t('onbReopenBtn')} →
        </button>
      </div>
    </div>
  );
}

function TrendBadge({ pct, tooltip }) {
  // UI-02 parça 2/5: Dün ile karşılaştırma badge'i
  if (pct === null || pct === undefined || !isFinite(pct)) return null;
  const rounded = Math.round(pct);
  const up = rounded > 0;
  const flat = rounded === 0;
  const cls = flat
    ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
    : up
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  const arrow = flat ? '▬' : up ? '▲' : '▼';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`} title={tooltip}>
      {arrow} %{Math.abs(rounded)}
    </span>
  );
}

function StatCard({ icon, label, value, color, onClick, trendPct, trendTooltip }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 flex-wrap">
            {label}
            <TrendBadge pct={trendPct} tooltip={trendTooltip} />
          </div>
          <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );
}

// Tur 274: Son İşlemler feed için relative time helper (Notifications.js ile uyumlu)
function timeAgoShort(input, t) {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const suffix = t('timeAgoSuffix', 'önce');
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return t('justNow', 'az önce');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}${t('shiftMinShort', 'dk')} ${suffix}`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}${t('shiftHourShort', 'sa')} ${suffix}`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}${t('subscriptionDaysUnit', 'g')} ${suffix}`;
}

// Tur 274: audit/order action → icon + insan-okur metin (i18n fallback ile)
function activityFromAudit(a, t) {
  const et = (a.entity_type || '').toLowerCase();
  const act = (a.action || '').toLowerCase();
  let icon = '📝';
  if (et.includes('order') || act.includes('order')) icon = '🛒';
  else if (et.includes('payment') || act.includes('payment')) icon = '💳';
  else if (et.includes('customer') || act.includes('customer')) icon = '👥';
  else if (et.includes('stock') || et.includes('ingredient') || act.includes('stock')) icon = '📦';
  const label = `${a.user_email || a.user_name || t('feedSystem', 'Sistem')} · ${a.action}${a.entity_id ? ` #${a.entity_id}` : ''}`;
  return { id: `a-${a.id}`, icon, label, time: a.created_at };
}

function pctChange(today, yesterday) {
  const t = parseFloat(today || 0);
  const y = parseFloat(yesterday || 0);
  if (y === 0) return t > 0 ? 100 : null;
  return ((t - y) / y) * 100;
}

// UI-02 parça 3/5: paket bağımsız SVG sparkline
function Sparkline({ data, color = '#10b981' }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const width = 600, height = 80, pad = 6;
  const peak = Math.max(...data, 1);
  const stepX = (width - pad * 2) / Math.max(data.length - 1, 1);
  const points = data
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = height - pad - (v / peak) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const area = `${pad},${height - pad} ${points} ${(width - pad).toFixed(1)},${height - pad}`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20" preserveAspectRatio="none">
      <polygon points={area} fill={color} fillOpacity="0.15" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Widget düzenleme modal'ı — show/hide tercihi.
function WidgetEditorModal({ open, onClose, t, enabledWidgets, setEnabledWidgets }) {
  const panelRef = useRef(null);
  const [draft, setDraft] = useState(() => new Set(enabledWidgets));

  useEffect(() => {
    if (open) setDraft(new Set(enabledWidgets));
  }, [open, enabledWidgets]);

  useFocusTrap(open, panelRef, { onEscape: onClose });

  if (!open) return null;

  const toggle = (id) => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    setEnabledWidgets(new Set(draft));
    onClose();
  };

  const handleReset = () => {
    setDraft(new Set(DASHBOARD_WIDGETS.map(w => w.id)));
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdrop}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-widget-editor-title"
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0 pr-3">
            <h2 id="dashboard-widget-editor-title" className="text-lg font-bold text-gray-900 dark:text-white">
              {t('dashWidgetEditTitle') || "Widget'ları Düzenle"}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('dashWidgetEditDesc') || "Görünmesini istemediğin widget'ları kapat"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close') || 'Kapat'}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {DASHBOARD_WIDGETS.map(w => {
            const checked = draft.has(w.id);
            return (
              <label
                key={w.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(w.id)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className={`flex-1 text-sm ${checked ? 'text-gray-800 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                  {t(w.tKey) || w.fallback}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-gray-400 font-mono">{w.id}</span>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          >
            {t('dashWidgetReset') || 'Varsayılana Dön'}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {t('cancel') || 'İptal'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {t('dashWidgetSaveBtn') || 'Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [yesterdayReport, setYesterdayReport] = useState(null);
  const [hourly, setHourly] = useState(null);   // UI-02 parça 3/5 — Pro+ plan
  const [topItems, setTopItems] = useState(null); // UI-02 parça 4/5 — Pro+ plan
  const [lowStock, setLowStock] = useState([]);
  const [readyOrdersCount, setReadyOrdersCount] = useState(null);   // KPI: açık siparişler
  const [pendingReservations, setPendingReservations] = useState(null); // KPI
  const [monthlyNetProfit, setMonthlyNetProfit] = useState(null); // KPI: bu ay net kâr (ERP-05)
  const [activeCouriers, setActiveCouriers] = useState(null);     // KPI: aktif kurye (ERP-01)
  const [activeShifts, setActiveShifts] = useState(null);         // KPI: bugün checked_in personel (ERP-06)
  const [profitTrend, setProfitTrend] = useState(null);           // sparkline: son 7 gün net kâr
  const [unassignedDeliveries, setUnassignedDeliveries] = useState(0); // aksiyon: kuryeye atanmamış teslimat
  const [wasteSummary, setWasteSummary] = useState(null); // Tur 130: haftalık ziyan
  const [recentAudits, setRecentAudits] = useState(null); // Tur 140: son admin işlemleri
  const [docs, setDocs] = useState(null);                  // Tur 237+: süresi yaklaşan/dolan HR belgeleri
  const [docsLoading, setDocsLoading] = useState(false);
  const [topWaiter, setTopWaiter] = useState(null);        // En Aktif Personel (Bugün) — yıldız personel
  const [topWaiterLoading, setTopWaiterLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Madde 12: chart period toggle (saatlik / günlük / haftalık)
  const [chartPeriod, setChartPeriod] = useState('hourly');
  const [dailyTrend, setDailyTrend] = useState(null);   // [{date, total}]
  const [weeklyTrend, setWeeklyTrend] = useState(null); // [{date, total}]
  // FIX-07: Hızlı Başlangıç checklist verileri (+ loading)
  const [setupCounts, setSetupCounts] = useState({ restaurantName: '', restaurantPhone: '', tables: 0, menuItems: 0, users: 0 });
  const [setupLoading, setSetupLoading] = useState(true);
  // Otomatik yenileme interval seçici (manual | 30s | 1m | 5m)
  const [refreshInterval, setRefreshInterval] = useState(() => {
    try { return localStorage.getItem('dashboard_refresh_interval') || 'manual'; } catch { return 'manual'; }
  });
  const [refreshMenuOpen, setRefreshMenuOpen] = useState(false);
  const refreshMenuRef = useRef(null);
  // Widget özelleştirme — show/hide + persist
  const [enabledWidgets, setEnabledWidgets] = useState(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_WIDGET_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch { /* parse hatası — varsayılan */ }
    return new Set(DASHBOARD_WIDGETS.map(w => w.id));
  });
  const [widgetEditorOpen, setWidgetEditorOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  // enabledWidgets değişince localStorage'a kaydet
  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_WIDGET_KEY, JSON.stringify([...enabledWidgets]));
    } catch { /* quota — yoksay */ }
  }, [enabledWidgets]);

  const isWidgetOn = (id) => enabledWidgets.has(id);

  const cancelledRef = useRef(false);
  const loadData = React.useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const yDate = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const monthStart = today.slice(0, 8) + '01'; // YYYY-MM-01
    const [tRes, oRes, pRes, sRes, yRes, hRes, topRes, rdyRes, rsvRes, pnlRes, courRes, shfRes, ptRes, delRes, wasteRes, audRes] = await Promise.allSettled([
      tablesAPI.getAll(),
      ordersAPI.getAll({ status: 'preparing' }),
      paymentsAPI.getDailyReport(today),
      stockAPI.getLowStock(),
      paymentsAPI.getDailyReport(yDate),
      reportsAPI.getHourly({ date: today }),        // UI-02 parça 3/5
      reportsAPI.getTopItems({ date: today, limit: 5 }), // UI-02 parça 4/5
      ordersAPI.getAll({ status: 'ready' }),        // KPI: açık sipariş (preparing + ready)
      reservationsAPI.getAll({ status: 'pending' }),// KPI: bekleyen rezervasyon
      reportsAPI.getProfitLoss({ start: monthStart, end: today }), // ERP-05 net kâr bu ay
      couriersAPI.getAll(),                          // ERP-01 aktif kurye sayısı
      shiftsAPI.getAssignments({ start: today, end: today }), // ERP-06 bugün vardiya
      reportsAPI.getProfitLossTrend({ days: 7 }),    // sparkline son 7 gün net kâr
      deliveryAPI.getOrders({ status: 'new,accepted,preparing' }), // aksiyon: kuryeye atanmamış teslimat
      stockAPI.getWasteSummary({ days: 7 }),         // Tur 130: haftalık ziyan
      auditLogsAPI.getAll({ limit: 10 }),            // Tur 140: son admin işlemleri (Tur 274: 10'a çıkarıldı, Son İşlemler feed)
    ]);
    if (cancelledRef.current) return;
    if (tRes.status === 'fulfilled') setTables(tRes.value.data.data || []);
    if (oRes.status === 'fulfilled') setOrders(oRes.value.data.data || []);
    if (pRes.status === 'fulfilled') setDailyReport(pRes.value.data.data || null);
    if (sRes.status === 'fulfilled') setLowStock(sRes.value.data.data || []);
    if (yRes.status === 'fulfilled') setYesterdayReport(yRes.value.data.data || null);
    if (hRes.status === 'fulfilled') {
      const hData = hRes.value.data.data || null;
      setHourly(hData);
      // Operasyonel insight: en yoğun saati bul (order_count > 0 olanlar arasında)
      if (Array.isArray(hData) && hData.length > 0) {
      }
    }
    if (topRes.status === 'fulfilled') setTopItems(topRes.value.data.data || null);
    // KPI: ready orders — endpoint yoksa veya 403 ise null kalır ve kart "—" gösterir
    if (rdyRes.status === 'fulfilled') {
      setReadyOrdersCount((rdyRes.value.data.data || []).length);
    }
    // KPI: pending rezervasyon — bugün ve sonrası filtrelenir; endpoint yoksa null
    if (rsvRes.status === 'fulfilled') {
      const rows = rsvRes.value.data.data || [];
      const todayStr = today;
      const upcoming = rows.filter(r => {
        const d = (r.reservation_date || r.date || '').slice(0, 10);
        return !d || d >= todayStr;
      });
      setPendingReservations(upcoming.length);
    }
    // KPI: bu ay net kâr (ERP-05 P&L) — endpoint 403 olabilir, sessiz null
    if (pnlRes.status === 'fulfilled') {
      const data = pnlRes.value?.data?.data;
      setMonthlyNetProfit(data?.net_profit ?? null);
    }
    // KPI: aktif kurye sayısı — endpoint yoksa null
    if (courRes.status === 'fulfilled') {
      const rows = courRes.value?.data?.data || [];
      setActiveCouriers(rows.filter(c => c.is_active).length);
    }
    // KPI: bugün checked_in personel sayısı (anlık çalışan)
    if (shfRes.status === 'fulfilled') {
      const raw = shfRes.value?.data;
      const rows = Array.isArray(raw) ? raw : (raw?.data || []);
      setActiveShifts(rows.filter(a => a.status === 'checked_in').length);
    }
    // Net kâr 7 gün sparkline serisi
    if (ptRes.status === 'fulfilled') {
      const rows = ptRes.value?.data?.data || [];
      setProfitTrend(rows.map(d => d.net_profit || 0));
    }
    // Aksiyon: kuryeye atanmamış aktif teslimat sayısı
    if (delRes.status === 'fulfilled') {
      const rows = delRes.value?.data?.data || [];
      const active = ['new', 'accepted', 'preparing', 'ready'];
      setUnassignedDeliveries(rows.filter(d => !d.courier_id && active.includes(d.status)).length);
    }
    // Tur 130: haftalık ziyan özeti
    if (wasteRes.status === 'fulfilled') {
      setWasteSummary(wasteRes.value?.data?.summary || null);
    }
    // Tur 140: son admin işlemleri (403/yetkisiz ise sessiz null)
    if (audRes.status === 'fulfilled') {
      const rows = audRes.value?.data?.data || audRes.value?.data || [];
      setRecentAudits(Array.isArray(rows) ? rows.slice(0, 5) : []);
    }
    // FIX-07: Setup checklist — sessiz fetch (hata durumunda 0/'' kalır, CTA görünür)
    Promise.allSettled([
      menuAPI.getItems(),
      usersAPI.getAll(),
      settingsAPI.get(),
    ]).then(([mRes, uRes, stRes]) => {
      if (cancelledRef.current) return;
      const restaurant = stRes.status === 'fulfilled' ? (stRes.value?.data?.data?.restaurant || {}) : {};
      setSetupCounts({
        restaurantName:  restaurant.name  || '',
        restaurantPhone: restaurant.phone || '',
        tables:    tRes.status === 'fulfilled' ? (tRes.value?.data?.data?.length || 0) : 0,
        menuItems: mRes.status === 'fulfilled' ? (mRes.value?.data?.data?.length || 0) : 0,
        users:     uRes.status === 'fulfilled' ? (uRes.value?.data?.data?.length || 0) : 0,
      });
      setSetupLoading(false);
    });
    // 403 (plan/abonelik sorunu) sessizce geçilir — SubscriptionBanner zaten gösterir
    const anyFailed = [tRes, oRes, pRes, sRes].some(
      r => r.status === 'rejected' && r.reason?.response?.status !== 403
    );
    if (anyFailed) setError(t('partialLoadError'));
    setLoading(false);
  }, [t]);

  useEffect(() => {
    cancelledRef.current = false;
    loadData();
    // UI-02 parça 5/5: SSE ana kanal, polling 60s → 180s (yedek)
    const interval = setInterval(loadData, 180000);
    return () => { clearInterval(interval); cancelledRef.current = true; };
  }, [loadData]);

  // Kullanıcı seçimli otomatik yenileme — localStorage tercihine göre interval kur
  useEffect(() => {
    try { localStorage.setItem('dashboard_refresh_interval', refreshInterval); } catch { /* quota — yoksay */ }
    const ms = refreshInterval === '30s' ? 30000 : refreshInterval === '1m' ? 60000 : refreshInterval === '5m' ? 300000 : 0;
    if (!ms) return undefined;
    const id = setInterval(() => loadData(), ms);
    return () => clearInterval(id);
  }, [refreshInterval, loadData]);

  // Yenile menüsü dışına tıklayınca kapat
  useEffect(() => {
    if (!refreshMenuOpen) return undefined;
    const onClick = (e) => {
      if (refreshMenuRef.current && !refreshMenuRef.current.contains(e.target)) setRefreshMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [refreshMenuOpen]);

  // Tur 237+: HR belge süreleri uyarı widget'ı — sadece admin için fetch
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    setDocsLoading(true);
    hrAPI.getDocuments({}).then(res => {
      if (cancelled) return;
      const all = res.data?.data || [];
      const warning = all.filter(d => d.expiry_status === 'expired' || d.expiry_status === 'expiring_soon');
      setDocs(warning);
    }).catch(() => { if (!cancelled) setDocs([]); }).finally(() => { if (!cancelled) setDocsLoading(false); });
    return () => { cancelled = true; };
  }, [user?.role]);

  // En Aktif Personel (Bugün) — admin için bugünün ciro lideri
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    setTopWaiterLoading(true);
    statsAPI.getReports?.()
      .then(res => {
        if (cancelled) return;
        const waiters = res.data?.data?.waiters || [];
        if (waiters.length) {
          const top = [...waiters].sort((a, b) => (parseFloat(b.revenue) || 0) - (parseFloat(a.revenue) || 0))[0];
          setTopWaiter(top);
        }
      })
      .catch(() => { if (!cancelled) setTopWaiter(null); })
      .finally(() => { if (!cancelled) setTopWaiterLoading(false); });
    return () => { cancelled = true; };
  }, [user?.role]);

  // Madde 12: günlük/haftalık trend lazy-fetch (period seçilince)
  useEffect(() => {
    if (chartPeriod === 'hourly') return;
    if (chartPeriod === 'daily' && dailyTrend) return;
    if (chartPeriod === 'weekly' && weeklyTrend) return;
    let cancelled = false;
    const days = chartPeriod === 'daily' ? 7 : 56;
    const today = new Date().toISOString().split('T')[0];
    const start = new Date(Date.now() - (days - 1) * 86400000).toISOString().split('T')[0];
    reportsAPI.getPaymentTrend({ start, end: today }).then(res => {
      if (cancelled) return;
      const rows = res?.data?.data || [];
      if (chartPeriod === 'daily') {
        const map = new Map(rows.map(r => [r.date, (r.cash || 0) + (r.card || 0) + (r.online || 0)]));
        const out = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(Date.now() - i * 86400000).toISOString().split('T')[0];
          out.push({ date: d, total: map.get(d) || 0 });
        }
        setDailyTrend(out);
      } else {
        // ISO haftaya grupla (Pazartesi başlangıçlı)
        const buckets = new Map();
        rows.forEach(r => {
          const date = new Date(r.date);
          const offset = (date.getDay() + 6) % 7; // Mon=0..Sun=6
          const monday = new Date(date.getTime() - offset * 86400000);
          const key = monday.toISOString().split('T')[0];
          const total = (r.cash || 0) + (r.card || 0) + (r.online || 0);
          buckets.set(key, (buckets.get(key) || 0) + total);
        });
        const sorted = [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b));
        setWeeklyTrend(sorted.map(([date, total]) => ({ date, total })));
      }
    }).catch(() => { /* 403/plan — sessizce geç */ });
    return () => { cancelled = true; };
  }, [chartPeriod, dailyTrend, weeklyTrend]);

  // UI-02 parça 5/5: tables/orders/kitchen/payments event'lerinde canlı yenile
  useSSE(({ type }) => {
    if (['tables', 'orders', 'kitchen', 'payments', 'all'].includes(type)) loadData();
  });

  const occupied = tables.filter(t => t.status === 'occupied').length;

  // UI-02: türetilmiş KPI'lar
  const totalRevenue = parseFloat(dailyReport?.total_amount || 0);
  const txCount = parseInt(dailyReport?.transaction_count || 0, 10);
  const avgTicket = txCount > 0 ? totalRevenue / txCount : 0;
  const occupancyPct = tables.length > 0 ? Math.round((occupied / tables.length) * 100) : 0;

  // UI-02 parça 2/5: dün ile karşılaştırma
  const yRevenue = parseFloat(yesterdayReport?.total_amount || 0);
  const yTxCount = parseInt(yesterdayReport?.transaction_count || 0, 10);
  const yAvgTicket = yTxCount > 0 ? yRevenue / yTxCount : 0;
  const revenueTrend = pctChange(totalRevenue, yRevenue);
  const orderTrend = pctChange(txCount, yTxCount);
  const avgTicketTrend = pctChange(avgTicket, yAvgTicket);

  // Tur 274: Son İşlemler feed — audit varsa onu kullan, yoksa orders'tan üret (max 10)
  const recentActivities = useMemo(() => {
    if (Array.isArray(recentAudits) && recentAudits.length > 0) {
      return recentAudits.slice(0, 10).map(a => activityFromAudit(a, t));
    }
    return (orders || []).slice(0, 10).map(o => ({
      id: `o-${o.id}`,
      icon: '🛒',
      label: `${t('feedOrderCreated', 'Sipariş')} #${o.id}${o.table_name ? ` · ${o.table_name}` : ''}`,
      time: o.created_at,
    }));
  }, [recentAudits, orders, t]);

  // Tur 310: order channels reduce — useMemo (was inline IIFE on every render)
  const orderChannelGroups = useMemo(() => {
    const src = dailyReport?.channels || dailyReport?.source_breakdown;
    const empty = { pos: { count: 0, revenue: 0 }, table: { count: 0, revenue: 0 }, online: { count: 0, revenue: 0 }, delivery: { count: 0, revenue: 0 } };
    if (src) {
      return { pos: src.pos || empty.pos, table: src.table || empty.table, online: src.online || empty.online, delivery: src.delivery || empty.delivery };
    }
    return (orders || []).reduce((acc, o) => {
      const s = (o.source || '').toLowerCase();
      const amt = parseFloat(o.total_amount || 0);
      const bucket = s === 'pos' ? 'pos' : s === 'table' ? 'table' : s === 'qr_online' ? 'online' : ['yemeksepeti','trendyol','getir','migros'].includes(s) ? 'delivery' : null;
      if (bucket) { acc[bucket].count += 1; acc[bucket].revenue += amt; }
      return acc;
    }, JSON.parse(JSON.stringify(empty)));
  }, [dailyReport, orders]);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">{t('loading')}</div>;

  // UI-02 ek: Dashboard KPI özeti (4 kart) — QuickStart'tan önce render edilir.
  const openOrdersValue =
    readyOrdersCount === null ? orders.length : orders.length + readyOrdersCount;
  const todaySalesValue = dailyReport
    ? `₺${parseFloat(dailyReport.total_amount || 0).toFixed(2)}`
    : null;
  const hourlySpark = Array.isArray(hourly) ? hourly.map(h => h.revenue || 0) : null;
  const formatCurrency = (val) => `₺${Number(val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const refreshOptions = [
    { key: 'manual', label: t('refreshManual', 'Manuel') },
    { key: '30s',    label: t('refresh30s',    '30 saniye') },
    { key: '1m',     label: t('refresh1m',     '1 dakika') },
    { key: '5m',     label: t('refresh5m',     '5 dakika') },
  ];
  const activeRefreshLabel = (refreshOptions.find(o => o.key === refreshInterval) || refreshOptions[0]).label;
  const refreshActive = refreshInterval !== 'manual';

  return (
    <div className="space-y-6">
      <h1 className="sr-only">{t('nav_dashboard') || 'Dashboard'}</h1>
      <SubscriptionBanner />
      {/* Dashboard üst başlık — otomatik yenileme interval seçici + widget düzenle */}
      <div className="flex items-center justify-end gap-2">
        {user?.role === 'admin' && (
          <button
            type="button"
            onClick={() => setWidgetEditorOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={t('dashWidgetEditBtn') || "Widget'ları Düzenle"}
            title={t('dashWidgetEditBtn') || "Widget'ları Düzenle"}
          >
            <span aria-hidden="true">⚙️</span>
            <span className="hidden sm:inline">{t('dashWidgetEditBtn') || 'Düzenle'}</span>
          </button>
        )}
        <div className="relative" ref={refreshMenuRef}>
          <button
            onClick={() => setRefreshMenuOpen(o => !o)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
            title={t('refreshTooltip', 'Otomatik yenileme')}
          >
            <span className={refreshActive ? 'inline-block animate-spin' : 'inline-block'} style={refreshActive ? { animationDuration: '2s' } : undefined}>🔄</span>
            <span>{t('refreshLabel', 'Yenile')}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">· {activeRefreshLabel}</span>
            <span className="text-xs">▾</span>
          </button>
          {refreshMenuOpen && (
            <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
              {refreshOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => { setRefreshInterval(opt.key); setRefreshMenuOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${refreshInterval === opt.key ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'}`}
                >
                  {refreshInterval === opt.key ? '✓ ' : ''}{opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* UI-02 ek: KPI özet kartları (Tur 121-123: 4 → 7 kart, Net Kâr + Aktif Kurye + Aktif Vardiya) */}
      {isWidgetOn('kpi_main') && (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <KPICard
          icon="💰"
          label={t('kpiTodaySales')}
          value={todaySalesValue}
          subLabel={t('kpiToday')}
          accent="green"
          loading={false}
          onClick={() => navigate('/payments')}
          spark={hourlySpark}
        />
        <KPICard
          icon="📋"
          label={t('kpiOpenOrders')}
          value={openOrdersValue}
          subLabel={t('kpiLive')}
          accent="amber"
          onClick={() => navigate('/orders')}
        />
        <KPICard
          icon="📦"
          label={t('kpiLowStock')}
          value={lowStock.length}
          subLabel={t('kpiLive')}
          accent={lowStock.length > 0 ? 'red' : 'green'}
          onClick={() => navigate('/stock')}
        />
        <KPICard
          icon="📅"
          label={t('kpiPendingReservations')}
          value={pendingReservations}
          subLabel={t('kpiToday')}
          accent="indigo"
          onClick={() => navigate('/reservations')}
        />
        <KPICard
          icon="📊"
          label={t('kpiMonthlyNetProfit')}
          value={monthlyNetProfit != null ? `₺${Number(monthlyNetProfit).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
          subLabel={t('kpiThisMonth')}
          accent={monthlyNetProfit == null ? 'indigo' : monthlyNetProfit >= 0 ? 'green' : 'red'}
          onClick={() => navigate('/reports')}
          spark={profitTrend && profitTrend.length > 1 ? profitTrend : undefined}
        />
        <KPICard
          icon="🛵"
          label={t('kpiActiveCouriers')}
          value={activeCouriers != null ? activeCouriers : '—'}
          subLabel={t('kpiLive')}
          accent={activeCouriers > 0 ? 'blue' : 'amber'}
          onClick={() => navigate('/couriers')}
        />
        <KPICard
          icon="👥"
          label={t('kpiActiveShifts')}
          value={activeShifts != null ? activeShifts : '—'}
          subLabel={t('kpiLive')}
          accent={activeShifts > 0 ? 'green' : 'amber'}
          onClick={() => navigate('/shifts')}
        />
      </div>
      )}
      {/* Tur 125+126 — Aksiyon Listesi: bugün yapılması gereken işler tek bakışta */}
      {isWidgetOn('action_items') && (lowStock.length > 0 || (pendingReservations || 0) > 0 || (readyOrdersCount || 0) > 0 || unassignedDeliveries > 0) && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-3 flex items-center gap-2">
            🎯 {t('actionItemsTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {readyOrdersCount > 0 && (
              <button
                onClick={() => navigate('/orders')}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-left transition-colors group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">🍳</span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 dark:text-gray-100">{readyOrdersCount} {t('actionReadyOrders')}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{t('actionReadyOrdersDesc')}</span>
                  </span>
                </span>
                <span className="text-gray-400 group-hover:text-blue-600 shrink-0">→</span>
              </button>
            )}
            {(pendingReservations || 0) > 0 && (
              <button
                onClick={() => navigate('/reservations')}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-left transition-colors group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">📅</span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 dark:text-gray-100">{pendingReservations} {t('actionPendingReservations')}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{t('actionPendingReservationsDesc')}</span>
                  </span>
                </span>
                <span className="text-gray-400 group-hover:text-blue-600 shrink-0">→</span>
              </button>
            )}
            {lowStock.length > 0 && (
              <button
                onClick={() => navigate('/stock')}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-left transition-colors group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">📦</span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 dark:text-gray-100">{lowStock.length} {t('actionCriticalStock')}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                      {lowStock.slice(0, 2).map(s => s.name).join(', ')}
                      {lowStock.length > 2 && ` +${lowStock.length - 2}`}
                    </span>
                  </span>
                </span>
                <span className="text-gray-400 group-hover:text-blue-600 shrink-0">→</span>
              </button>
            )}
            {unassignedDeliveries > 0 && (
              <button
                onClick={() => navigate('/delivery-orders')}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-left transition-colors group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">🛵</span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 dark:text-gray-100">{unassignedDeliveries} {t('actionUnassignedDeliveries')}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{t('actionUnassignedDeliveriesDesc')}</span>
                  </span>
                </span>
                <span className="text-gray-400 group-hover:text-blue-600 shrink-0">→</span>
              </button>
            )}
          </div>
        </div>
      )}
      {/* Operasyonel insight — Yoğun Saatler renk-kodlu SVG bar chart */}
      {isWidgetOn('peak_hours') && (() => {
        // Veri kaynağı tercihi: 1) hourly state (gerçek API) 2) orders[] created_at bin 3) demo fallback
        let counts = new Array(24).fill(0);
        let dataSource = 'demo'; // 'hourly' | 'orders' | 'demo'
        if (Array.isArray(hourly) && hourly.length > 0) {
          hourly.forEach(h => {
            const hr = Number(h.hour);
            if (hr >= 0 && hr < 24) counts[hr] = h.order_count || 0;
          });
          dataSource = 'hourly';
        } else if (Array.isArray(orders) && orders.length > 0) {
          orders.forEach(o => {
            const ts = o.created_at || o.createdAt;
            if (!ts) return;
            const d = new Date(ts);
            if (isNaN(d.getTime())) return;
            const hr = d.getHours();
            if (hr >= 0 && hr < 24) counts[hr] += 1;
          });
          // Eğer hiç bin dolmadıysa demo'ya düş
          if (counts.every(c => c === 0)) {
            counts = new Array(24).fill(0);
            [11, 12, 13, 19, 20].forEach(hr => { counts[hr] = hr === 13 || hr === 20 ? 12 : 8; });
            dataSource = 'demo';
          } else {
            dataSource = 'orders';
          }
        } else {
          // Demo fallback — peak: 11/12/13/19/20
          [11, 12, 13, 19, 20].forEach(hr => { counts[hr] = hr === 13 || hr === 20 ? 12 : 8; });
        }
        const maxCount = Math.max(...counts, 1);
        const peakIdx = counts.indexOf(maxCount);
        // SVG ölçek: viewBox 240x80, 24 bar
        const VB_W = 240, VB_H = 80;
        const CHART_H = 64; // bar alanı
        const AXIS_H = 12;
        const slot = VB_W / 24; // 10
        const barW = slot - 1; // 9
        const colorFor = (count) => {
          if (maxCount === 0) return '#86efac'; // green-300
          const pct = count / maxCount;
          if (pct > 0.75) return '#ef4444';     // red-500 — peak
          if (pct >= 0.40) return '#eab308';    // yellow-500 — orta
          return '#22c55e';                     // green-500 — düşük
        };
        const sourceLabel = dataSource === 'hourly'
          ? (t('peakHourSourceLive') || 'Canlı veri')
          : dataSource === 'orders'
          ? (t('peakHourSourceOrders') || 'Sipariş geçmişinden')
          : (t('peakHourSourceDemo') || 'Demo veri');
        return (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <span aria-hidden="true">🔥</span>
                {t('peakHourTitle') || 'Yoğun Saatler'}
                {maxCount > 0 && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    — {String(peakIdx).padStart(2, '0')}:00 · {counts[peakIdx]} {t('orderCountSuffix') || 'sipariş'}
                  </span>
                )}
              </h2>
              <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {sourceLabel}
              </span>
            </div>
            <div role="img" aria-label={t('peakHourChartAria') || 'Saat bazında sipariş yoğunluğu'}>
              <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full" style={{ height: '120px' }} preserveAspectRatio="none">
                {counts.map((count, hr) => {
                  const h = maxCount > 0 ? (count / maxCount) * CHART_H : 0;
                  const x = hr * slot + 0.5;
                  const y = CHART_H - h;
                  const fill = colorFor(count);
                  return (
                    <rect
                      key={hr}
                      x={x}
                      y={y}
                      width={barW}
                      height={h}
                      fill={fill}
                      rx="1"
                    >
                      <title>{`${String(hr).padStart(2, '0')}:00 — ${count} ${t('orderCountSuffix') || 'sipariş'}`}</title>
                    </rect>
                  );
                })}
                {/* Alt eksen: her 4 saatte bir etiket (0,4,8,12,16,20) */}
                {[0, 4, 8, 12, 16, 20].map(hr => (
                  <text
                    key={hr}
                    x={hr * slot + slot / 2}
                    y={CHART_H + AXIS_H - 2}
                    fontSize="6"
                    textAnchor="middle"
                    fill="#9ca3af"
                    fontFamily="monospace"
                  >
                    {String(hr).padStart(2, '0')}
                  </text>
                ))}
              </svg>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {[
                { color: '#ef4444', label: t('peakHourLegendHigh') || 'Yoğun (>%75)' },
                { color: '#eab308', label: t('peakHourLegendMid') || 'Orta (%40–75)' },
                { color: '#22c55e', label: t('peakHourLegendLow') || 'Sakin (<%40)' },
              ].map(leg => (
                <span key={leg.label} className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: leg.color }} aria-hidden="true" />
                  {leg.label}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
      {/* Bugünkü Sipariş Kanalları — POS / Masa / Online / Delivery dağılımı */}
      {isWidgetOn('order_channels') && (() => {
        // Tur 310: orderChannelGroups hoisted to component-scope useMemo
        const groups = orderChannelGroups;
        const totalCount = groups.pos.count + groups.table.count + groups.online.count + groups.delivery.count;
        if (totalCount === 0) return null;
        const channels = [
          { key: 'pos',      label: t('dashChannelPOS')      || 'POS',      icon: '🧾', bar: 'bg-purple-500',  text: 'text-purple-600 dark:text-purple-400',  ...groups.pos },
          { key: 'table',    label: t('dashChannelTable')    || 'Masa',     icon: '🍽️', bar: 'bg-blue-500',    text: 'text-blue-600 dark:text-blue-400',      ...groups.table },
          { key: 'online',   label: t('dashChannelOnline')   || 'Online',   icon: '🌐', bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ...groups.online },
          { key: 'delivery', label: t('dashChannelDelivery') || 'Paket',    icon: '🛵', bar: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',    ...groups.delivery },
        ];
        return (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
              📊 {t('dashOrderChannelsTitle') || 'Bugünkü Sipariş Kanalları'}
              <span className="text-xs font-normal text-gray-400">({totalCount})</span>
            </h2>
            <div className="flex w-full h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 mb-4">
              {channels.map(c => c.count > 0 && (
                <div key={c.key} className={c.bar} style={{ width: `${(c.count / totalCount) * 100}%` }} title={`${c.label}: ${c.count}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {channels.map(c => {
                const pct = totalCount > 0 ? Math.round((c.count / totalCount) * 100) : 0;
                return (
                  <div key={c.key} className="border border-gray-100 dark:border-gray-700 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${c.bar}`} />
                      <span>{c.icon} {c.label}</span>
                      <span className="ml-auto font-mono text-[10px]">%{pct}</span>
                    </div>
                    <div className={`text-lg font-bold mt-1 ${c.text}`}>{c.count}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">{formatCurrency(c.revenue)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {/* Tur 130 — Haftalık ziyan/giriş widget (stock_adjustments derived) */}
      {isWidgetOn('waste_summary') && wasteSummary && (wasteSummary.total_waste_cost > 0 || wasteSummary.total_incoming_cost > 0) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              📊 {t('wasteWidgetTitle')} <span className="text-xs font-normal text-gray-400">({wasteSummary.days}g)</span>
            </h2>
            <button onClick={() => navigate('/stock')} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">{t('wasteWidgetGoStock')} →</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-3">
              <div className="text-[11px] text-rose-700 dark:text-rose-400">{t('wasteTotal')}</div>
              <div className="text-lg font-bold text-rose-700 dark:text-rose-300">₺{Number(wasteSummary.total_waste_cost).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-rose-600 dark:text-rose-400">{wasteSummary.items_with_waste} {t('wasteItemsCount')}</div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
              <div className="text-[11px] text-emerald-700 dark:text-emerald-400">{t('wasteIncoming')}</div>
              <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">₺{Number(wasteSummary.total_incoming_cost).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-emerald-600 dark:text-emerald-400">{t('wasteIncomingDesc')}</div>
            </div>
          </div>
          {wasteSummary.top_waste && wasteSummary.top_waste.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{t('wasteTopList')}</div>
              <div className="space-y-1.5">
                {wasteSummary.top_waste.map((w, i) => (
                  <div key={w.ingredient_id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-200 truncate">
                      <span className="text-xs text-gray-400 mr-1.5">{i + 1}.</span>{w.name}
                    </span>
                    <span className="text-rose-600 dark:text-rose-400 font-medium whitespace-nowrap ml-2">
                      −{w.waste_qty.toFixed(1)} {w.unit} · ₺{w.waste_cost.toFixed(0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {isWidgetOn('activity_feed') && <ActivityFeed />}
      {/* Tur 140: Son Admin İşlemleri (recent audit logs) */}
      {isWidgetOn('recent_audits') && recentAudits !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-1.5">
              <span aria-hidden="true">📜</span>
              {t('recentAuditTitle')}
            </h2>
            <button
              onClick={() => navigate('/audit-logs')}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              {t('viewAll')} →
            </button>
          </div>
          {recentAudits.length > 0 ? (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {recentAudits.map(a => {
                const when = a.created_at ? new Date(a.created_at) : null;
                const timeStr = when ? when.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <li key={a.id} className="py-2 flex items-center gap-3 text-xs">
                    <span className="text-gray-400 font-mono whitespace-nowrap flex-shrink-0 w-20">{timeStr}</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-200 truncate min-w-0 flex-shrink-0 max-w-[40%]">
                      {a.user_email || a.user_name || '—'}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 truncate flex-1">
                      <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[10px] font-mono mr-1.5">{a.action}</span>
                      {a.entity_type && <span className="text-gray-400">{a.entity_type}{a.entity_id ? `#${a.entity_id}` : ''}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-5" role="status">
              <div className="text-3xl mb-2 opacity-70" aria-hidden="true">📜</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('dashActivityEmpty') || 'Henüz aktivite yok'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
                {t('dashActivityEmptyDesc') || 'Son işlemler burada gözükecek.'}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Belge Süreleri Widget'ı — Tur 237+: HR belge expiry uyarı (sadece admin) */}
      {isWidgetOn('doc_expiry') && user?.role === 'admin' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            📂 {t('dashDocExpiryTitle') || 'Belge Süreleri'}
            <Link to="/hr" className="text-xs text-blue-600 hover:underline ml-auto font-normal">{t('dashViewAll') || 'tümü →'}</Link>
          </h2>
          {docsLoading ? (
            <div className="text-sm text-gray-400">{t('loading') || 'Yükleniyor...'}</div>
          ) : docs?.length ? (
            <div className="space-y-2">
              {docs.slice(0, 5).map(d => (
                <div key={d.id} className="flex items-start justify-between text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.title}</div>
                    <div className="text-xs text-gray-500 truncate">{d.employee_name} {d.employee_surname}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ml-2 ${
                    d.expiry_status === 'expired'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}>
                    {d.expiry_status === 'expired' ? `⚠️ ${t('dashDocExpired') || 'Dolmuş'}` : '⏰ ' + d.expiry_date}
                  </span>
                </div>
              ))}
              {docs.length > 5 && <div className="text-xs text-gray-500">+{docs.length - 5} {t('dashDocMore') || 'belge daha…'}</div>}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-2xl mb-1 opacity-60">✅</div>
              <div className="text-xs text-gray-500">{t('dashDocExpiryEmpty') || 'Süresi yaklaşan/dolan belge yok'}</div>
            </div>
          )}
        </div>
      )}
      {/* En Aktif Personel (Bugün) — admin yıldız personel widget */}
      {isWidgetOn('top_waiter') && user?.role === 'admin' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            🏆 {t('dashTopWaiterTitle') || 'Bugünkü Yıldız Personel'}
          </h2>
          {topWaiterLoading ? (
            <div className="text-sm text-gray-400">{t('loading') || 'Yükleniyor...'}</div>
          ) : topWaiter ? (
            <div className="text-center">
              <div className="text-5xl mb-2">🥇</div>
              <div className="font-bold text-lg text-gray-900 dark:text-white">{topWaiter.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {topWaiter.order_count} {t('orderCountSuffix') || 'sipariş'} · {formatCurrency(topWaiter.revenue)}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-2xl mb-1 opacity-60">📊</div>
              <div className="text-xs text-gray-500">{t('dashNoOrdersToday') || 'Bugün henüz sipariş yok'}</div>
            </div>
          )}
        </div>
      )}
      {isWidgetOn('quick_start') && <QuickStartChecklist t={t} counts={setupCounts} loading={setupLoading} navigate={navigate} />}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex justify-between">
          {error}<button onClick={() => setError('')}>✕</button>
        </div>
      )}
      {/* Stats — UI-02: 4 → 6 kart (ortalama sepet + doluluk oranı eklendi) */}
      {isWidgetOn('stats_overview') && (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon="🍽️" label={t('kpiTotalTables')} value={tables.length} color="text-blue-600" onClick={() => navigate('/tables')} />
        <StatCard icon="🔴" label={t('kpiOccupiedTables')} value={occupied} color="text-red-500" onClick={() => navigate('/tables')} />
        <StatCard
          icon="📊"
          label={t('kpiOccupancy')}
          value={`%${occupancyPct}`}
          color={occupancyPct >= 80 ? 'text-red-500' : occupancyPct >= 50 ? 'text-amber-500' : 'text-emerald-500'}
          onClick={() => navigate('/tables')}
        />
        <StatCard
          icon="📋"
          label={t('kpiActiveOrders')}
          value={orders.length}
          color="text-yellow-500"
          onClick={() => navigate('/orders')}
          trendPct={orderTrend}
          trendTooltip={t('trendBadgeTooltip')}
        />
        <StatCard
          icon="🧾"
          label={t('kpiAvgTicket')}
          value={`₺${avgTicket.toFixed(2)}`}
          color="text-indigo-500"
          onClick={() => navigate('/payments')}
          trendPct={avgTicketTrend}
          trendTooltip={t('trendBadgeTooltip')}
        />
        <StatCard
          icon="💰"
          label={t('kpiTodayRevenue')}
          value={`₺${totalRevenue.toFixed(2)}`}
          color="text-green-600"
          onClick={() => navigate('/payments')}
          trendPct={revenueTrend}
          trendTooltip={t('trendBadgeTooltip')}
        />
      </div>
      )}

      {/* UI-02 parça 3/5 + Madde 12: Ciro chart toggle (Saatlik/Günlük/Haftalık) */}
      {isWidgetOn('revenue_chart') && hourly !== null && (() => {
        const data = chartPeriod === 'hourly'
          ? (hourly.map(h => h.revenue))
          : chartPeriod === 'daily'
            ? (dailyTrend?.map(d => d.total) || [])
            : (weeklyTrend?.map(w => w.total) || []);
        const title = chartPeriod === 'hourly' ? t('chartHourly') : chartPeriod === 'daily' ? t('chartDaily') : t('chartWeekly');
        const subtitle = chartPeriod === 'hourly' ? t('chartHourlyHint') : chartPeriod === 'daily' ? t('chartDailyHint') : t('chartWeeklyHint');
        const color = chartPeriod === 'hourly' ? '#10b981' : chartPeriod === 'daily' ? '#3b82f6' : '#8b5cf6';
        const hasData = data.some(v => v > 0);
        const isLoading = (chartPeriod === 'daily' && !dailyTrend) || (chartPeriod === 'weekly' && !weeklyTrend);
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">📈 {title}</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">{subtitle}</span>
                <div role="tablist" aria-label={t('chartPeriodLabel')} className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                  {[
                    { key: 'hourly', label: t('periodHourly') },
                    { key: 'daily', label: t('periodDaily') },
                    { key: 'weekly', label: t('periodWeekly') },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      role="tab"
                      aria-selected={chartPeriod === opt.key}
                      onClick={() => setChartPeriod(opt.key)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        chartPeriod === opt.key
                          ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {isLoading ? (
              <div className="h-20 flex items-center justify-center text-xs text-gray-400">{t('loading')}</div>
            ) : hasData ? (
              <>
                <Sparkline data={data} color={color} />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-mono">
                  {chartPeriod === 'hourly' ? (
                    <><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></>
                  ) : chartPeriod === 'daily' ? (
                    dailyTrend.map((d, i) => (
                      <span key={i}>{new Date(d.date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}</span>
                    ))
                  ) : (
                    weeklyTrend.map((w, i) => (
                      <span key={i}>{new Date(w.date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}</span>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-5" role="status">
                <div className="text-3xl mb-2 opacity-70" aria-hidden="true">📈</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('chartNoData')}</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* UI-02 parça 4/5: Top 5 ürün */}
      {isWidgetOn('top_items') && topItems !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('topItemsTitle')}</h2>
          {topItems.length > 0 ? (
            <div className="space-y-2">
              {topItems.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-gray-400 w-5 flex-shrink-0">{idx + 1}.</span>
                    <span className="text-gray-800 dark:text-gray-100 truncate">{item.name}</span>
                  </span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-gray-500 dark:text-gray-400">{item.qty} {t('unitPieces')}</span>
                    <span className="font-medium text-green-600 w-20 text-right">₺{item.revenue.toFixed(2)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-6" role="status">
              <div className="text-3xl mb-2 opacity-70" aria-hidden="true">🍽️</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('dashTopItemsEmpty') || 'Bugün satış yok'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
                {t('dashTopItemsEmptyDesc') || 'Sipariş geldikçe en çok satanlar burada listelenir.'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Low Stock Alert */}
      {isWidgetOn('low_stock_alert') && lowStock.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <h2 className="font-semibold text-red-700 dark:text-red-400 mb-2">{t('criticalStockAlert')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {lowStock.map(item => (
              <div key={item.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg px-3 py-2 text-sm">
                <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                <span className="text-red-600 font-medium">{item.quantity} {item.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isWidgetOn('active_orders') && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Active Orders */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">{t('sectionActiveOrders')}</h2>
            <button onClick={() => navigate('/orders')} className="text-sm text-blue-600 hover:underline">{t('viewAll')}</button>
          </div>
          {orders.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">{t('noActiveOrders')}</p>
          ) : (
            <div className="space-y-2">
              {orders.slice(0, 5).map(order => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{order.table_name || `${t('tableNo')} #${order.table_id}`}</div>
                    <div className="text-xs text-gray-400">{new Date(order.created_at).toLocaleTimeString(undefined)}</div>
                  </div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">₺{parseFloat(order.total_amount || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Daily Report */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">{t('dailySummary')}</h2>
          {dailyReport ? (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('totalTransactions')}</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{dailyReport.transaction_count || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('cash')}</span>
                <span className="font-medium text-green-600">₺{parseFloat(dailyReport.cash_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('card')}</span>
                <span className="font-medium text-blue-600">₺{parseFloat(dailyReport.card_total || 0).toFixed(2)}</span>
              </div>
              <div className="border-t dark:border-gray-700 pt-2 flex justify-between text-sm font-semibold">
                <span className="text-gray-700 dark:text-gray-300">{t('totalRevenueLabel')}</span>
                <span className="text-gray-800 dark:text-white">₺{parseFloat(dailyReport.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">{t('noPaymentsToday')}</p>
          )}
        </div>
      </div>
      )}

      {/* Tur 274: Son İşlemler activity feed — audit veya orders'tan türetilir, max 10, scroll */}
      {isWidgetOn('recent_activity') && recentActivities.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <span aria-hidden="true">🕒</span> {t('dashRecentActivity', 'Son İşlemler')}
          </h2>
          <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {recentActivities.map(it => (
              <li key={it.id} className="py-2 flex items-center gap-3 text-sm">
                <span className="text-xl flex-shrink-0" aria-hidden="true">{it.icon}</span>
                <span className="flex-1 text-gray-700 dark:text-gray-200 truncate">{it.label}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                  {timeAgoShort(it.time, t)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick Actions */}
      {isWidgetOn('quick_actions') && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">{t('quickActions')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: '🍽️', label: t('nav_tables'), to: '/tables' },
            { icon: '📋', label: t('nav_orders'), to: '/orders' },
            { icon: '👨‍🍳', label: t('nav_kitchen'), to: '/kitchen' },
            { icon: '📦', label: t('nav_stock'), to: '/stock' },
          ].map(item => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
            >
              <span className="text-2xl">{item.icon}</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
      )}

      <WidgetEditorModal
        open={widgetEditorOpen}
        onClose={() => setWidgetEditorOpen(false)}
        t={t}
        enabledWidgets={enabledWidgets}
        setEnabledWidgets={setEnabledWidgets}
      />
    </div>
  );
}
